import { DependencyList, useCallback, useMemo } from 'react'
import {
  targetListApi,
  targetListApiBy
} from 'interface/endpoints'
import {
  ApiGetWithToken,
	ApiDeleteWithToken,
	ApiPatchWithToken,
	ApiPostWithToken,
	useApiFetch,
	useApiFetchList,
	useApiPost,
	useApiPostList
} from 'shared/api'

import {
	AddTargetListFormData,
	AddTargetListResult,
	TargetListFormData,
  TargetListResult,
  TargetListResultList,
	TargetReasonResult
} from './types'
import { flatten } from 'shared/util'

export * from './types'

export const useListTargetList = (
	searchId: number | undefined | null,
	option: {
		partialLimit?: number
		onPreFetch?: () => void
		onFetched?: (result: TargetListResult[]) => void
	} = {},
	deps?: DependencyList
) => {
	const { partialLimit, onPreFetch, onFetched } = option
	return useApiFetchList<TargetListResult, TargetListResult[]>(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId) return 0
				if (onPreFetch) onPreFetch()
				return ApiGetWithToken<number>(
					apiAccess,
					targetListApi(searchId, '/count'),
					cancelToken
				)
			},
			[searchId, onPreFetch]
		),
		useCallback(
			(apiAccess, cancelToken, offset, limit) => {
				if (!searchId) return null
				const wrapper = async () => {
					const list = await ApiGetWithToken<TargetListResultList>(
						apiAccess,
						targetListApi(searchId, '/list', { offset, limit }),
						cancelToken
					)
					return list?.list
				}
				return wrapper()
			},
			[searchId]
		),
		useCallback(
			(result) => {
				if (onFetched) onFetched(result)
				return result
			},
			[onFetched]
		),
		partialLimit ?? 100,
		deps
	)
}

export const useGetTargetList = (
  searchId?: number | null,
  targetListId?: number | null
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
      if (!searchId || !targetListId) return null
      return ApiGetWithToken<TargetListResult>(
        apiAccess,
        targetListApiBy(searchId, targetListId),
        cancelToken
  		)
    },
		[searchId, targetListId]
	)
)

export const useGetTargetListReason = (
  searchId?: number | null,
  targetListId?: number | null
) => useApiFetch(
	useCallback(
		(apiAccess, cancelToken) => {
      if (!searchId || !targetListId) return null
      return ApiGetWithToken<TargetReasonResult>(
        apiAccess,
        targetListApiBy(searchId, targetListId, '/reason'),
        cancelToken
  		)
    },
		[searchId, targetListId]
	)
)

export const usePostTargetListComplex = (
  searchId: number, 
	callback: {
    onPosted?: (targetList: TargetListResult, result: AddTargetListResult) => void
    onError?: (reason: Error | undefined) => void
  } = {}
) => {
	const { onPosted, onError } = callback
	
	type PostListResult = {
		targetList: TargetListResult
		addListResult: AddTargetListResult
	}
	const onUploaded = useCallback(
		(params: PostListResult) => {
			if (onPosted) onPosted(params.targetList, params.addListResult)
		},
		[onPosted]
	)
	const [uploadState, upload] = usePostUploadTargetList<PostListResult>(searchId, {
		onError,
		onPosted: onUploaded
	})

	const onPostedList = useCallback(
		(addListResult: AddTargetListResult, targetList: TargetListResult) => {
			upload({
				targetListId: targetList.listId,
				params: { targetList, addListResult }
			})
		},
		[upload]
	)
	const [postListState, startListPost] = usePostAddTargetList<TargetListResult>(searchId, {
		onError,
		onPosted: onPostedList
	})

	const onPostedEntity = useCallback(
		(targetList: TargetListResult, params: { dataForm: AddTargetListFormData }) => {
			const { dataForm } = params
			startListPost({
				targetListId: targetList.listId,
				form: dataForm,
				params: targetList
			}, dataForm.data.length, 1000)
		},
		[startListPost]
	)
	const [postEntityState, postEntity] = usePostTargetList(searchId, {
		onError,
		onPosted: onPostedEntity
	})

	const post = useCallback(
		(form: TargetListFormData, dataForm: AddTargetListFormData) => {
			postEntity({ form, params: { dataForm }})
		},
		[postEntity]
	)

	return useMemo(() => ({
		posting: postEntityState.posting || postListState.posting || uploadState.posting,
		error: flatten([ postEntityState.error, postListState.error, uploadState.error ]),
		percent: (postListState.posting && postListState.count > 0) ? ((postListState.current * 100) / postListState.count) : undefined,
		post
	}), [
		postEntityState,
		postListState,
		uploadState,
		post
	])
}

export const usePostTargetList = <TParams>(
  searchId: number, 
	callback?: {
    onPosted?: (targetList: TargetListResult, params: TParams) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<{ form: TargetListFormData, params: TParams }, TargetListResult>(
	useCallback(
		(apiAccess, cancelToken, { form }, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
				targetListApi(searchId),
				form,
				cancelToken,
				webSocketId
			)
		},
		[searchId]
	),
	useCallback(
		(result, { params }) => {
			if (callback?.onPosted) callback.onPosted(result, params)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
			if (callback?.onError) callback.onError(reason)
		},
		[callback]
	)
)

export const usePostAddTargetList = <TParams>(
	searchId: number, 
  callback?: {
    onPosted?: (result: AddTargetListResult, params: TParams) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPostList<{
	targetListId: number
	form: AddTargetListFormData
	params: TParams
}, AddTargetListResult>(
	useCallback(
		(apiAccess, { targetListId, form }, from, to, cancelToken, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
        targetListApiBy(searchId, targetListId, '/add'),
				from === 0 ? {
					identifier: form.identifier,
					offset: 0,
					category: form.category,
					score: form.score,
					data: form.data.slice(from, to)
				} : {
					// 2回目以降はデータのみを指定する
					identifier: form.identifier,
					offset: from,
					data: form.data.slice(from, to)
				},
				cancelToken,
				webSocketId
			)
		},
		[searchId]
	),
	useCallback(
		(results, { params }) => {
			const summary: AddTargetListResult = {
				lastSequence: 0,
				importCount: 0,
				notFoundDcfKojin: [],
				notFoundPersonalId: []
			}
			for(const result of results) {
				summary.lastSequence = Math.max(summary.lastSequence, result.lastSequence)
				summary.importCount += result.importCount
				summary.notFoundDcfKojin.push(...result.notFoundDcfKojin)
				summary.notFoundPersonalId.push(...result.notFoundPersonalId)
			}
			if (callback?.onPosted) callback.onPosted(summary, params)
			return summary
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useEditTargetList = (
	searchId: number,
	targetListId: number, 
  callback: {
    onEditted?: (result: TargetListResult) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<TargetListFormData, TargetListResult>(
	useCallback(
		(apiAccess, cancelToken, form, webSocketId) => {
			return ApiPatchWithToken(
				apiAccess,
				targetListApiBy(searchId, targetListId),
				form,
				cancelToken,
				webSocketId
			)
		},
		[searchId, targetListId]
	),
	useCallback(
		(result) => {
			if (callback?.onEditted) callback.onEditted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
			if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useDeleteTargetList = (
  searchId: number, 
	callback?: {
    onDeleted?: (targetListId: number) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<number, number>(
	useCallback(
		(apiAccess, cancelToken, targetListId, webSocketId) => {
			return ApiDeleteWithToken(
				apiAccess,
        targetListApiBy(searchId, targetListId),
				cancelToken,
				webSocketId
			)
		},
		[searchId]
	),
	useCallback(
		(result) => {
			if (callback?.onDeleted) callback.onDeleted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const usePostUploadTargetList = <TParams>(
	searchId: number, 
  callback?: {
    onPosted?: (params: TParams) => void
    onError?: (reason: Error | undefined) => void
  }
) => useApiPost<{ targetListId: number, params: TParams }, number>(
	useCallback(
		(apiAccess, cancelToken, { targetListId }, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
        targetListApiBy(searchId, targetListId, '/upload'),
				undefined,
				cancelToken,
				webSocketId
			)
		},
		[searchId]
	),
	useCallback(
		(result, { params }) => {
			if (callback?.onPosted) callback.onPosted(params)
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

